/**
 * Site-wide entry module (loaded by snippets/scripts.liquid on every page). Kept tiny on purpose:
 * - `.gs-btn` fill that grows from the pointer entry point (mockup `fxOver` / `fxOut`);
 * - no « save image » menu on a long press on a photo (touch only: desktop right click is kept);
 * - a link ending in `#inscription` (Instagram bio, a button of the site) opens Klaviyo's signup popup;
 * - lazy-loads the heavier features only when the page contains them (the footer mini-game only when it comes near
 *   the screen), and re-runs them for sections re-rendered in the theme editor.
 */

import { setPointerVars } from '@theme/utils';

/**
 * Feature modules, by the selector that needs them, and when to load them: with the page, or once one of the
 * elements comes near the screen (the footer mini-game: no need to download and start it on every page view).
 * Each module exports `init(root)`.
 */
export const FEATURES = /** @type {const} */ ([
  [
    '[data-split],[data-intro],[data-pop],[data-rise],[data-float],[data-par],[data-draw],[data-rv],[data-marquee],[data-plset],[data-stkset],.faq details,details[data-acc]',
    () => import('@theme/motion'),
    'now',
  ],
  ['[data-pc]', () => import('@theme/product-card'), 'now'],
  ['canvas[data-honey]', () => import('@theme/honey'), 'now'],
  ['canvas[data-game]', () => import('@theme/jar-game'), 'near'],
]);

/**
 * Resolves once one of `els` comes within 400 px of the screen (at once without IntersectionObserver).
 *
 * @param {Iterable<Element>} els
 * @returns {Promise<void>}
 */
export function nearScreen(els) {
  if (!('IntersectionObserver' in window)) return Promise.resolve();
  return new Promise((resolve) => {
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        resolve();
      },
      { rootMargin: '400px' },
    );
    for (const el of els) io.observe(el);
  });
}

/**
 * Loads and runs every feature whose selector matches inside `root`.
 *
 * @param {ParentNode} root
 * @returns {Promise<void>}
 */
export async function loadFeatures(root) {
  await Promise.all(
    FEATURES.filter(([selector]) => root.querySelector(selector)).map(async ([selector, load, when]) => {
      if (when === 'near') await nearScreen(root.querySelectorAll(selector));
      try {
        const mod = await load();
        mod.init(root);
      } catch (error) {
        console.error(error);
      }
    }),
  );
}

/**
 * @param {Event} event
 * @param {boolean} entering
 */
function onButtonPointer(event, entering) {
  const e = /** @type {PointerEvent} */ (event);
  const target = e.target instanceof Element ? e.target.closest('.gs-btn') : null;
  if (!(target instanceof HTMLElement)) return;
  if (e.relatedTarget instanceof Node && target.contains(e.relatedTarget)) return;
  setPointerVars(target, e, entering);
}

/**
 * Whether a context menu must be cancelled: long press (touch) on a photo. iOS ignores `contextmenu` there,
 * base.css handles it with `-webkit-touch-callout: none`.
 *
 * @param {EventTarget | null} target
 * @param {string} pointerType - Type of the last `pointerdown`.
 */
export function blocksImageMenu(target, pointerType) {
  return pointerType === 'touch' && target instanceof Element && !!target.closest('img, picture');
}

/** Hash of the links that open Klaviyo's signup popup (`settings.klaviyo_signup_form`, `<html data-kl-form>`). */
export const SIGNUP_HASH = '#inscription';

/**
 * Opens Klaviyo's popup form `formId`, once the age gate is answered (`html.ag-on`: its modal dialog would hide the
 * popup). Queued: klaviyo.js (app embed) may not be loaded yet.
 *
 * @param {string} formId
 */
export function openSignup(formId) {
  const root = document.documentElement;
  const open = () => (window._klOnsite ??= []).push(['openForm', formId]);
  if (!root.classList.contains('ag-on')) return void open();
  const mo = new MutationObserver(() => {
    if (root.classList.contains('ag-on')) return;
    mo.disconnect();
    open();
  });
  mo.observe(root, { attributes: true, attributeFilter: ['class'] });
}

const signupForm = document.documentElement.dataset.klForm;
if (signupForm) {
  if (location.hash === SIGNUP_HASH) {
    history.replaceState(null, '', location.pathname + location.search);
    openSignup(signupForm);
  }
  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest(`a[href$="${SIGNUP_HASH}"]`) : null;
    if (!link) return;
    e.preventDefault();
    openSignup(signupForm);
  });
}

let lastPointer = '';
document.addEventListener('pointerdown', (e) => (lastPointer = e.pointerType), { passive: true });
document.addEventListener('contextmenu', (e) => {
  if (blocksImageMenu(e.target, lastPointer)) e.preventDefault();
});
document.addEventListener('pointerover', (e) => onButtonPointer(e, true));
document.addEventListener('pointerout', (e) => onButtonPointer(e, false));
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof HTMLElement) loadFeatures(e.target);
});

loadFeatures(document);
